import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import SolidHeartIcon from 'LuxKit/Icons/solid/SolidHeartIcon'
import type { ComponentProps } from 'react'
import { memo, useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { tripLoginModalSignUpView } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import Badge from 'LuxKit/Badge'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useInView } from 'react-intersection-observer'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import FirstTripItemOnboardingTooltip from 'tripPlanner/components/Drawer/FirstTripItemOnboardingTooltip'
import { useTrips } from 'tripPlanner/hooks/api/trip'
import { useUserSettings } from 'tripPlanner/hooks/api/userSettings'
import { clearFirstTripItemHasJustBeenAdded } from 'tripPlanner/reducers/actions'
import { useWishlistIndex } from 'wishlists/hooks'
import useWishlistsAndChatReleaseState from 'wishlists/hooks/useWishlistsAndChatReleaseState'
import NavBarButton from '../NavBarButton/NavBarButton'

const TripNavButton = styled(NavBarButton)`

    ${mediaQueryUp.tablet} {
      display: flex;
    }

`

type InheritedNavBarButtonProps = Pick<ComponentProps<typeof NavBarButton>, 'contentBreakpointMax'>

interface Props extends InheritedNavBarButtonProps {
  className?: string
}

function WishlistNavButton(props: Props) {
  const intl = useIntl()
  const { data } = useWishlistIndex()
  const hasSavedItems = Boolean(
    data?.wishlists.find((wishlist) => wishlist.isDefault)?.itemCount,
  )

  const wishlistText = intl.formatMessage({
    id: '8tn_OvJmXrdKVNnw4EQO',
    defaultMessage: 'Wishlist',
    description: 'Wishlist navigation button text in the website header',
  })

  return (
    <TripNavButton
      {...props}
      icon={hasSavedItems ? <SolidHeartIcon colour="favourite" /> : <LineHeartIcon />}
      contentBreakpointMin="desktop"
      to="/wishlists"
    >
      {wishlistText}
    </TripNavButton>
  )
}

function ClassicTripNavButton(props: Props) {
  const intl = useIntl()
  const navRef = useRef<HTMLButtonElement>(null)
  const dispatch = useAppDispatch()
  const { data: trips = [] } = useTrips()
  const { data: userSettings } = useUserSettings()
  const userHasBeenAddedToNewTrips = !!userSettings?.recentlyAddedToTripIDs?.length
  const tripItemHasJustBeenAdded = useAppSelector(state => state.tripPlanner.tripItemHasJustBeenAdded)
  const firstItemJustBeenAdded = useAppSelector(state => state.tripPlanner.firstTripItemHasJustBeenAdded)
  const hasSavedItems = !!trips.find(trip => trip.itemCount > 0)

  const onNavClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(async() => {
    fireInteractionEvent(tripLoginModalSignUpView('saved_drawer'))
  }, [])

  const [menuItemRef, menuItemInView] = useInView({ triggerOnce: true, skip: !firstItemJustBeenAdded })
  const showTripNotificationDot = userHasBeenAddedToNewTrips || tripItemHasJustBeenAdded
  const showTooltip = menuItemInView && firstItemJustBeenAdded

  const onDismissFirstTripTooltip = useCallback(() => {
    dispatch(clearFirstTripItemHasJustBeenAdded())
  }, [dispatch])

  useEffect(() => {
    menuItemRef(navRef.current)
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [navRef.current])

  const tripPlansText = intl.formatMessage({
    id: 'PlZDImO9d6ZLP7trPiLB',
    defaultMessage: 'Trip Plans',
    description: 'Trip Plans navigation button text in the trip planner feature',
  })

  return (
    <TripGuard>
      <TripNavButton
        {...props}
        icon={hasSavedItems ? <SolidHeartIcon colour="favourite" /> : <LineHeartIcon />}
        contentBreakpointMin="desktop"
        to="/trip-planner"
        onClick={onNavClick}
        ref={navRef}
        badge={showTripNotificationDot ? <Badge kind="dot" mode="critical" /> : undefined}
      >
        {tripPlansText}
      </TripNavButton>
      {showTooltip && trips[0] && (
        <FirstTripItemOnboardingTooltip
          isOpen
          anchorRef={navRef}
          placement="bottom"
          trip={trips[0]}
          onDismiss={onDismissFirstTripTooltip}
        />
      )}
    </TripGuard>
  )
}

function NavBarTripButton(props: Props) {
  const { shouldUseWishlists } = useWishlistsAndChatReleaseState()

  if (shouldUseWishlists) {
    return <WishlistNavButton {...props} />
  }

  return <ClassicTripNavButton {...props} />
}

export default memo(NavBarTripButton)
